import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { LayoutBase } from 'rc-dock';
import { TrackerTypes } from '../../globals';
import { FactoryPresetLayouts } from '../../components/workspace/WorkspaceLayouts';
import { IWindowInfo, IWorkspaceLayout } from '../../interfaces/Workspace';

export interface ICameraPreviewData
{
	cameraId?: string;
	aspectRatio?: number;
}

export interface IWorkspaceState
{
	desktopWindowUiInfo: Record<string, IWindowInfo>;
	layout: LayoutBase;
	customLayouts: {
		factoryPresetLayouts: IWorkspaceLayout[];
		userPresetLayouts: IWorkspaceLayout[];
	};
	activeWindow: string | null;
	activeTab: string | null;
	modals: Record<string, any>;
	notifications: Record<string, any>;
	visibleSceneViewIds: string[];
	hoveredWindowId: string;
	mainSceneViewId: string;
	isBusy: boolean;
	lastSelectedTrackerType: TrackerTypes | null;
	gridVisible: boolean;
	trackersVisible: boolean;
	spritesVisible: boolean;
	shouldRefreshProjectList: boolean;
	previewCameraData: ICameraPreviewData;
	expandedProjects: Array<string>;
}

const initialState: IWorkspaceState = {
	desktopWindowUiInfo: {},
	layout: FactoryPresetLayouts[0].layout,
	customLayouts: {
		factoryPresetLayouts: [...FactoryPresetLayouts],
		userPresetLayouts: []
	},
	activeWindow: null,
	activeTab: null,
	modals: {},
	notifications: {},
	visibleSceneViewIds: [],
	hoveredWindowId: "",
	mainSceneViewId: "",
	isBusy: false,
	lastSelectedTrackerType: null,
	gridVisible: true,
	trackersVisible: true,
	spritesVisible: true,
	shouldRefreshProjectList: false,
	previewCameraData: {
		cameraId: undefined,
		aspectRatio: undefined,
	},
	expandedProjects: [],
};

const WorkspaceSlice = createSlice({
	name: "workspace",
	initialState,
	reducers: {
		/**
		 * set JSON layout for rc-dock
		 * @param {*} action payloud should be a valid rc-dock layout definition
		 */
		setLayout(state, action: PayloadAction<LayoutBase>)
		{
			if (action.payload?.dockbox)
			{
				state.layout = action.payload;
			}
		},
		/**
		 * add a named custom layout definition for rc-dock - if a layout with that name already exists, override it
		 * @param {*} action payloud should be an object with a name field and a layout field, holding a valid rc-dock layout definition
		 */
		addCustomLayout(state, action: PayloadAction<{ name: string, layout: IWorkspaceLayout }>)
		{
			const existingCustomLayout = state.customLayouts.userPresetLayouts.find((item) => item.name === action.payload.name);
			if (existingCustomLayout)
			{
				existingCustomLayout.layout = action.payload.layout.layout;
			}
			else
			{
				state.customLayouts.userPresetLayouts.push(action.payload.layout);
			}
		},
		/**
		 * delete a named custom layout definition
		 * @param {*} action payloud should be an object with a name field
		 */
		deleteCustomLayout(state, action: PayloadAction<{ name: string }>)
		{
			state.customLayouts.userPresetLayouts = state.customLayouts.userPresetLayouts.filter((item) => item.name !== action.payload.name);
		},
		/**
		 * replaces all user-defined custom layouts with the given array
		 * @param {*} action payload should be an array
		 */
		setCustomLayouts(state, action: PayloadAction<IWorkspaceLayout[]>)
		{
			state.customLayouts.userPresetLayouts = [...action.payload];
		},
		/**
		 * set indication of the currently active (input-receiving) window
		 * @param {*} action payloud should be the window's ID
		 */
		setActiveWindow(state, action: PayloadAction<{ id: string }>)
		{
			state.activeWindow = action.payload.id;
		},
		setActiveTab(state, action: PayloadAction<string>)
		{
			state.activeTab = action.payload;
		},
		clear(state, action: PayloadAction<undefined>)
		{
			state.desktopWindowUiInfo = {};
			state.hoveredWindowId = "";
			state.activeWindow = null;
			state.mainSceneViewId = "";
		},
		reset(state, action: PayloadAction<undefined>)
		{
			return initialState;
		},
		addModal(state, action: PayloadAction<string>)
		{
			state.modals[action.payload] = action.payload; // payload = modal id
		},
		removeModal(state, action: PayloadAction<string>)
		{
			delete state.modals[action.payload]; // payload = modal id
		},
		clearModals(state, action: PayloadAction<undefined>)
		{
			state.modals = {};
		},
		addNotification(state, action: PayloadAction<string>)
		{
			state.notifications[action.payload] = action.payload; // payload = notification id
		},
		removeNotification(state, action: PayloadAction<string>)
		{
			delete state.notifications[action.payload]; // payload = notification id
		},
		clearNotifications(state, action: PayloadAction<undefined>)
		{
			state.notifications = {};
		},
		setHoveredWindow(state, action: PayloadAction<{ id: string }>)
		{
			state.hoveredWindowId = action.payload.id;
		},
		setMainSceneView(state, action: PayloadAction<{ id: string }>)
		{
			state.mainSceneViewId = action.payload.id;
		},
		setIsBusy(state, action: PayloadAction<boolean>)
		{
			state.isBusy = action.payload;
		},
		setLastSelectedTrackerType(state, action: PayloadAction<TrackerTypes>)
		{
			state.lastSelectedTrackerType = action.payload;
		},
		setGridVisible(state, action: PayloadAction<boolean>)
		{
			state.gridVisible = action.payload;
		},
		setTrackersVisible(state, action: PayloadAction<boolean>)
		{
			state.trackersVisible = action.payload;
		},
		setSpritesVisible(state, action: PayloadAction<boolean>)
		{
			state.spritesVisible = action.payload;
		},
		setShouldRefreshProjectList(state, action: PayloadAction<boolean>)
		{
			state.shouldRefreshProjectList = action.payload;
		},
		setPreviewCameraData(state, action: PayloadAction<Partial<ICameraPreviewData>>)
		{
			state.previewCameraData = { ...state.previewCameraData, ...action.payload };
		},
		addExpandedProject(state, action: PayloadAction<string>)
		{
			state.expandedProjects.push(action.payload);
		},
		removeExpandedProject(state, action: PayloadAction<string>)
		{
			state.expandedProjects = state.expandedProjects.filter((project) => project !== action.payload);
		},
		clearExpandedProjects(state, action: PayloadAction<undefined>)
		{
			state.expandedProjects = [];
		},
	},
});

export default WorkspaceSlice;
